import React from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart } from 'lucide-react';
interface PremiumCTAButtonProps {
  className?: string;
  size?: 'md' | 'lg' | 'xl';
  href?: string;
}
export function PremiumCTAButton({
  className = '',
  size = 'xl',
  href = 'https://mikioscodigital.com/cart/51834224541977:1'
}: PremiumCTAButtonProps) {
  const sizeClasses = {
    md: 'px-6 py-3 text-sm md:px-8 md:py-4 md:text-base',
    lg: 'px-8 py-4 text-base md:px-12 md:py-5 md:text-lg',
    xl: 'px-10 py-5 text-base md:px-16 md:py-6 md:text-xl'
  };
  return <a href={href} target="_blank" rel="noopener noreferrer" className="inline-block w-full md:w-auto" data-id="element-618">
      <div className="relative inline-block w-full md:w-auto" data-id="element-619">
        {/* Animated gold/orange multicolor glow rings */}
        <motion.div className="absolute inset-0 rounded-xl md:rounded-2xl" animate={{
        boxShadow: ['0 0 15px 3px rgba(255, 165, 0, 0.8), 0 0 30px 8px rgba(255, 215, 0, 0.6), 0 0 45px 12px rgba(255, 140, 0, 0.4)', '0 0 20px 5px rgba(255, 215, 0, 0.8), 0 0 40px 10px rgba(255, 140, 0, 0.6), 0 0 60px 15px rgba(255, 165, 0, 0.4)', '0 0 18px 4px rgba(255, 140, 0, 0.8), 0 0 35px 9px rgba(255, 165, 0, 0.6), 0 0 50px 13px rgba(255, 215, 0, 0.4)', '0 0 15px 3px rgba(255, 165, 0, 0.8), 0 0 30px 8px rgba(255, 215, 0, 0.6), 0 0 45px 12px rgba(255, 140, 0, 0.4)']
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-620" />

        {/* Pulsing gold/orange gradient background */}
        <motion.div className="absolute inset-0 rounded-xl md:rounded-2xl bg-gradient-to-r from-orange-500 via-yellow-500 to-amber-600 opacity-90" animate={{
        scale: [1, 1.05, 1],
        opacity: [0.9, 1, 0.9]
      }} transition={{
        duration: 1.5,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-621" />

        {/* Main button with gold/orange gradient */}
        <motion.div className={`relative ${sizeClasses[size]} font-bold uppercase tracking-wide md:tracking-wider rounded-xl md:rounded-2xl
            bg-gradient-to-r from-amber-500 via-orange-500 to-yellow-600
            text-white shadow-2xl
            transform transition-all duration-300
            hover:scale-105 active:scale-95
            flex items-center gap-2 md:gap-3 justify-center
            border-2 md:border-4 border-yellow-300/40
            cursor-pointer
            w-full
            ${className}`} whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} animate={{
        backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
      }} transition={{
        backgroundPosition: {
          duration: 3,
          repeat: Infinity,
          ease: 'linear'
        }
      }} style={{
        backgroundSize: '200% 200%',
        textShadow: '0 0 10px rgba(0,0,0,0.5), 0 0 20px rgba(255,215,0,0.4), 0 2px 4px rgba(0,0,0,0.3)'
      }} data-id="element-622">
          {/* Animated cart icon - responsive size */}
          <motion.div animate={{
          rotate: [0, -10, 10, -10, 0],
          scale: [1, 1.2, 1]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-623">
            <ShoppingCart className="w-5 h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 drop-shadow-lg flex-shrink-0" data-id="element-624" />
          </motion.div>

          {/* Responsive text - shorter on mobile */}
          <span className="relative drop-shadow-lg leading-tight" data-id="element-625">
            <span className="hidden sm:inline" data-id="element-626">
              SÍ, QUIERO MI PACK COMPLETO AHORA!!!
            </span>
            <span className="inline sm:hidden" data-id="element-627">¡QUIERO MI PACK AHORA!</span>
          </span>

          {/* Gold sparkle effects - responsive positioning */}
          <motion.div className="absolute -top-0.5 md:-top-1 -right-0.5 md:-right-1 w-3 h-3 md:w-4 md:h-4 bg-yellow-300 rounded-full" animate={{
          scale: [0, 1.5, 0],
          opacity: [0, 1, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeOut'
        }} data-id="element-628" />
          <motion.div className="absolute -bottom-0.5 md:-bottom-1 -left-0.5 md:-left-1 w-2 h-2 md:w-3 md:h-3 bg-orange-400 rounded-full" animate={{
          scale: [0, 1.5, 0],
          opacity: [0, 1, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeOut',
          delay: 1
        }} data-id="element-629" />
          <motion.div className="absolute top-1/2 -left-1 md:-left-2 w-1.5 h-1.5 md:w-2 md:h-2 bg-amber-300 rounded-full" animate={{
          scale: [0, 1.3, 0],
          opacity: [0, 1, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeOut',
          delay: 0.5
        }} data-id="element-630" />
        </motion.div>

        {/* 3D depth effect - orange/gold bottom layer */}
        <div className="absolute inset-0 rounded-xl md:rounded-2xl bg-gradient-to-br from-orange-800 to-amber-900 transform translate-y-1.5 md:translate-y-2 -z-10 blur-sm opacity-70" data-id="element-631" />
      </div>
    </a>;
}